import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BookOpen, ChevronLeft, Download, FileText, Pentagon, Plus, RotateCcw, Search, X, Zap } from 'lucide-react';

import Autoposition from '../../../assets/Autoposition.svg?react';
import { usePdfExport } from '../../../context/PdfExportContext.tsx';
import { useExport } from '../../../context/useExport';
import { useAppSelector } from '../../../hooks';
import { getUserFeatureFlags } from '../../../store/selectors/featureFlags.selector';
import { selectActiveVersion } from '../../../store/selectors/workspace.selector.ts';
import { FRONTEND_PDF_PLOTS_FLAG } from '../../../utils/featureFlagKeys';
import { selectWorkspaceMeasurementData } from '../../../utils/measurementCalculation/measurementSource';
import { type ExpectedActualPair, isTargetSatisfied } from '../../../utils/normSatisfactionUtils.ts';
import { MaxAngleControl } from '../MaxAngleControl';
import { useAppliedMarketMatch } from '../useAppliedMarketMatch';
import { AppliedMatchCard } from './AppliedMatchCard';
import { MountingHeights } from './MountingHeights';
import { type SetupIssue, planNextStep } from './nextStep';
import {
  type GoToSection,
  MARKET_PREFILTERS_FORM_ID,
  type NextStep,
  type PanelSectionId,
  type StepAction,
  type StepMessage,
} from './types';
import { useWorkspaceActions } from './useWorkspaceActions';

interface Options {
  activeSection: PanelSectionId;
  goTo: GoToSection;
  /** Whether the market-match prefilters form is valid (it owns that state). */
  marketCanStart: boolean;
}

/** Turns the plan for the version (see `planNextStep`) into footer messages and buttons. */
export function useWorkspaceNextStep({ activeSection, goTo, marketCanStart }: Options): NextStep {
  const { t } = useTranslation();
  const actions = useWorkspaceActions();
  const version = useAppSelector(selectActiveVersion);
  const projectName = useAppSelector((s) => s.workspace.project?.name);
  const lamps = useAppSelector((s) => s.workspace.lamps);
  const layers = useAppSelector((s) => s.workspace.modelLayers);
  const isOptimizationStarting = useAppSelector((s) => s.workspace.isOptimizationStarting);
  const isAutoPositioningStarting = useAppSelector((s) => s.workspace.isAutoPositioningStarting);
  const isCancelingAutoPositioning = useAppSelector((s) => s.workspace.isCancelingAutoPositioning);
  const isCreatingVersion = useAppSelector((s) => s.workspace.isCreatingVersion);
  const isMarketMatchStarting = useAppSelector((s) => s.workspace.isMarketMatchStarting);
  const frontendPdfPlotsEnabled = useAppSelector((s) => getUserFeatureFlags(s).includes(FRONTEND_PDF_PLOTS_FLAG));
  const measurementData = useAppSelector(selectWorkspaceMeasurementData);
  const planeDraw = useAppSelector((s) => s.measurementPlanes.draw);
  const exclusionDraw = useAppSelector((s) => s.measurementPlanes.exclusionDraw);
  const { pdfUrl, isExportLoading, isGeneratingPdf } = usePdfExport();
  const { numPages } = useExport();
  const appliedMatch = useAppliedMarketMatch();
  // Layers are filled from the version's meshes right after the project arrives; until then the
  // CHL / LMN checks would run on an empty list.
  const loading = !version || ((version.meshes?.length ?? 0) > 0 && layers.length === 0);
  // Autoposition asks for a check of the mounting heights first (shown in the footer).
  const [confirmingAutoposition, setConfirmingAutoposition] = useState(false);

  const plan = useMemo(
    () =>
      planNextStep({
        activeSection,
        version,
        lamps,
        layers,
        frontendPdfPlotsEnabled,
        pending: {
          optimization: isOptimizationStarting,
          autopositioning: isAutoPositioningStarting,
          cancelling: isCancelingAutoPositioning,
          creatingVersion: isCreatingVersion,
          marketMatch: isMarketMatchStarting,
        },
        pdf: { ready: !!pdfUrl, generating: isExportLoading || isGeneratingPdf },
        marketCanStart,
        targets: countTargets(measurementData),
        surfaceDraw: exclusionDraw.active
          ? { mode: 'exclusion', points: exclusionDraw.verts.length }
          : planeDraw.active
            ? { mode: 'surface', points: planeDraw.verts.length }
            : { mode: null, points: 0 },
        appliedMatch: { active: appliedMatch.active, canCreateVersion: appliedMatch.canCreateVersion },
        loading,
      }),
    [
      activeSection,
      version,
      lamps,
      layers,
      frontendPdfPlotsEnabled,
      isOptimizationStarting,
      isAutoPositioningStarting,
      isCancelingAutoPositioning,
      isCreatingVersion,
      isMarketMatchStarting,
      pdfUrl,
      isExportLoading,
      isGeneratingPdf,
      marketCanStart,
      measurementData,
      planeDraw,
      exclusionDraw,
      appliedMatch.active,
      appliedMatch.canCreateVersion,
      loading,
    ],
  );

  const canAutoposition = plan.stage === 'setup' && plan.action === 'autoposition' && plan.ready && plan.canModify;
  useEffect(() => {
    if (!canAutoposition) setConfirmingAutoposition(false);
  }, [canAutoposition]);

  return useMemo<NextStep>(() => {
    // "Show" links jump to the section that fixes the message (none when it is already open).
    const show = (section: PanelSectionId) =>
      activeSection === section ? undefined : { label: t('workspace.panel.show'), onClick: () => goTo(section) };

    const exportTooltip =
      plan.stage === 'market-matched' && !frontendPdfPlotsEnabled
        ? t('workspace.export.notAvailableMarketMatched')
        : t('workspace.export.notAvailable');
    const exportAction = (enabled: boolean): StepAction => ({
      id: 'export',
      label: t('workspace.panel.next.exportDocs'),
      icon: <FileText size={16} />,
      onClick: () => goTo('export'),
      disabled: !enabled,
      tooltip: enabled ? undefined : exportTooltip,
    });

    const issueMessage = (issue: SetupIssue): StepMessage => {
      // CHL / LMN problems are fixed with the toggles, so flash them on the way there.
      const toToggles = { label: t('workspace.panel.show'), onClick: () => goTo('layers', 'toggles') };
      switch (issue.kind) {
        case 'mark-chl':
          return { id: issue.kind, tone: 'warning', text: t('workspace.panel.next.markChl'), link: toToggles };
        case 'mark-lmn':
          return { id: issue.kind, tone: 'warning', text: t('workspace.panel.next.markLmn'), link: toToggles };
        case 'choose-norm':
          return {
            id: issue.kind,
            tone: 'warning',
            text: t('workspace.panel.next.chooseNorm', { count: issue.count }),
            link: show('layers'),
          };
        case 'lamps-not-above':
          return {
            id: issue.kind,
            tone: 'error',
            text: t('workspace.panel.next.lampsNotAbove', { names: issue.names.join(', ') }),
            link: show('luminaires'),
          };
        case 'max-lamps':
          return { id: issue.kind, tone: 'warning', text: t('workspace.addLuminaireManuallyDisabled') };
      }
    };

    switch (plan.stage) {
      case 'loading':
        return {
          messages: [],
          primary: { id: 'loading', label: t('common.loading'), loading: true, disabled: true },
        };

      case 'export':
        return {
          messages: [
            plan.generating
              ? { id: 'pdf', tone: 'progress', text: t('workspace.pleaseWaitPdf') }
              : { id: 'pdf', tone: 'info', text: t('workspace.exportingPages', { count: numPages || 0 }) },
          ],
          primary: {
            id: 'download',
            label: plan.generating ? t('workspace.generatingPdf') : t('workspace.panel.next.downloadPdf'),
            icon: <Download size={16} />,
            loading: plan.generating,
            disabled: !plan.pdfReady || plan.generating,
            onClick: () => {
              if (!pdfUrl) return;
              const link = document.createElement('a');
              link.href = pdfUrl;
              link.download = `${projectName || 'report'}_export.pdf`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            },
          },
        };

      case 'market-search':
        if (plan.running) {
          return { messages: [{ id: 'market', tone: 'progress', text: t('workspace.panel.next.marketRunning') }] };
        }
        return {
          messages: plan.failed
            ? [{ id: 'market-failed', tone: 'error', text: t('workspace.panel.next.marketFailed') }]
            : [],
          primary: {
            id: 'market-start',
            label: plan.starting ? t('workspace.preparing') : t('workspace.startMarketMatch'),
            icon: <Search size={16} />,
            formId: MARKET_PREFILTERS_FORM_ID,
            disabled: !plan.canStart,
            loading: plan.starting,
            tooltip: plan.canStart ? undefined : t('workspace.marketMatchPrefiltersSelectManufacturers'),
          },
        };

      case 'surfaces':
        if (plan.drawing) {
          return {
            messages: [
              {
                id: 'drawing',
                tone: 'info',
                text: t(
                  plan.drawing === 'exclusion'
                    ? 'workspace.panel.surfaces.cuttingHint'
                    : 'workspace.panel.surfaces.drawingHint',
                  { count: plan.points },
                ),
              },
            ],
            secondary: {
              id: 'cancel-drawing',
              label: t('workspace.panel.surfaces.cancelDrawing'),
              icon: <X size={16} />,
              onClick: actions.cancelSurfaceDrawing,
            },
          };
        }
        return {
          messages: [],
          primary: {
            id: 'draw-surface',
            label: t('workspace.panel.surfaces.draw'),
            icon: <Pentagon size={16} />,
            onClick: actions.startSurfaceDrawing,
          },
        };

      case 'applied-match':
        return {
          messages: [],
          content: <AppliedMatchCard />,
          primary: plan.canCreateVersion
            ? {
                id: 'match-to-version',
                label: t('workspace.createVersionWithConfiguration'),
                onClick: appliedMatch.createVersion,
                loading: plan.creatingVersion,
                disabled: plan.creatingVersion,
              }
            : undefined,
          secondary: {
            id: 'unapply',
            label: t('workspace.unapply'),
            icon: <X size={16} />,
            onClick: appliedMatch.unapply,
            disabled: plan.creatingVersion,
          },
        };

      case 'market-results':
        return {
          messages: [{ id: 'apply-hint', tone: 'info', text: t('workspace.panel.next.applyHint') }],
          // The whole catalogue, in a new tab so the scene and the matches stay as they are.
          secondary: {
            id: 'browse-catalogue',
            label: t('workspace.panel.next.browseCatalogue'),
            icon: <BookOpen size={16} />,
            variant: 'secondary',
            onClick: () => window.open('/catalogue', '_blank', 'noopener'),
          },
        };

      case 'optimizing':
        return {
          messages: [{ id: 'calc', tone: 'progress', text: t('workspace.panel.next.optimizationRunning') }],
          content: <MaxAngleControl />,
          primary: {
            id: 'optimizing',
            label: plan.starting ? t('workspace.preparing') : t('workspace.panel.next.optimizing'),
            loading: true,
            disabled: true,
          },
        };

      case 'autopositioning':
        return {
          messages: [
            {
              id: 'autoposition',
              tone: 'progress',
              text: plan.cancelling ? t('workspace.panel.next.cancelling') : t('workspace.panel.next.autopositioning'),
            },
          ],
          primary: {
            id: 'autopositioning',
            label: plan.starting ? t('workspace.preparing') : t('workspace.autopositioning'),
            loading: true,
            disabled: true,
          },
          secondary: plan.starting
            ? undefined
            : {
                id: 'cancel',
                label: t('workspace.panel.next.cancel'),
                icon: <X size={16} />,
                variant: 'danger',
                onClick: actions.cancelAutoposition,
                disabled: plan.cancelling,
              },
        };

      case 'failed':
        return {
          messages: [
            { id: 'failed', tone: 'error', text: t('workspace.panel.next.failed') },
            { id: 'failed-hint', tone: 'info', text: t('workspace.panel.next.failedHint') },
          ],
          primary: {
            id: 'retry',
            label: t('workspace.panel.next.retryInNewVersion'),
            icon: <RotateCcw size={16} />,
            onClick: actions.retryInNewVersion,
            loading: plan.creatingVersion,
            disabled: plan.creatingVersion,
          },
        };

      case 'completed': {
        const messages: StepMessage[] = [
          {
            id: 'done',
            tone: 'success',
            text:
              plan.targets.total > 0
                ? t('workspace.panel.next.completedTargets', plan.targets)
                : t('workspace.panel.next.completed'),
            link: show('results'),
          },
        ];
        if (plan.baking) messages.push({ id: 'baking', tone: 'progress', text: t('workspace.panel.next.baking') });
        if (plan.marketRunning) {
          messages.push({ id: 'market', tone: 'progress', text: t('workspace.panel.next.marketRunning') });
        }
        if (!plan.exportEnabled) messages.push({ id: 'export', tone: 'info', text: exportTooltip });
        // Until a market search has run, matching real catalogue luminaires is THE next step and
        // export steps back to the secondary button.
        if (plan.promoteMarket) {
          messages.push({ id: 'market-next', tone: 'next', text: t('workspace.panel.next.marketNext') });
          return {
            messages,
            primary: {
              id: 'market',
              label: t('workspace.panel.next.findRealLuminaires'),
              icon: <Search size={16} />,
              onClick: () => goTo('market'),
            },
            secondary: { ...exportAction(plan.exportEnabled), variant: 'secondary' },
          };
        }
        return {
          messages,
          primary: exportAction(plan.exportEnabled),
          secondary: plan.offerMarket
            ? {
                id: 'market',
                label: plan.hasCandidates
                  ? t('workspace.panel.next.marketMatches')
                  : t('workspace.panel.next.findOnMarket'),
                icon: <Search size={16} />,
                onClick: () => goTo('market'),
              }
            : undefined,
        };
      }

      case 'market-matched': {
        const messages: StepMessage[] = [];
        if (plan.unassigned > 0) {
          messages.push({
            id: 'assign',
            tone: 'warning',
            text: t('workspace.panel.next.assignProducts', { count: plan.unassigned }),
            link: show('luminaires'),
          });
        }
        if (!plan.exportEnabled) messages.push({ id: 'export', tone: 'info', text: exportTooltip });
        return { messages, primary: exportAction(plan.exportEnabled) };
      }

      case 'setup': {
        if (confirmingAutoposition && plan.action === 'autoposition') {
          return {
            messages: [{ id: 'confirm', tone: 'info', text: t('workspace.panel.next.confirmHeights') }],
            content: <MountingHeights compact />,
            primary: {
              id: 'autoposition-confirm',
              label: t('workspace.panel.next.confirmAutoposition'),
              icon: <Autoposition width={16} height={16} />,
              onClick: () => {
                setConfirmingAutoposition(false);
                actions.startAutoposition();
              },
            },
            secondary: {
              id: 'back',
              label: t('workspace.panel.next.back'),
              icon: <ChevronLeft size={16} />,
              onClick: () => setConfirmingAutoposition(false),
            },
          };
        }
        const messages = plan.issues.map(issueMessage);
        if (plan.ready && plan.action === 'autoposition') {
          messages.push({
            id: 'ready',
            tone: 'info',
            text: t('workspace.panel.next.readyAutoposition', { chl: plan.chlCount, lmn: plan.lmnCount }),
          });
        } else if (plan.ready) {
          messages.push({
            id: 'ready',
            tone: 'info',
            text: t('workspace.panel.next.readyOptimization', { count: plan.lampCount }),
          });
        }
        const blocked = {
          disabled: !plan.ready || !plan.canModify,
          tooltip: plan.ready ? undefined : t('workspace.panel.next.completeStepsFirst'),
        };
        return {
          messages,
          content: plan.action === 'optimize' ? <MaxAngleControl /> : undefined,
          primary:
            plan.action === 'autoposition'
              ? {
                  id: 'autoposition',
                  label: t('workspace.panel.next.autoposition'),
                  icon: <Autoposition width={16} height={16} />,
                  onClick: () => setConfirmingAutoposition(true),
                  ...blocked,
                }
              : {
                  id: 'optimize',
                  label: t('workspace.panel.next.runOptimization'),
                  icon: <Zap size={16} />,
                  onClick: actions.startOptimization,
                  ...blocked,
                },
          secondary: {
            id: 'add',
            label: t('workspace.addLuminaireManually'),
            iconOnly: true,
            icon: <Plus size={18} />,
            onClick: actions.addLampManually,
            disabled: !plan.canModify || plan.maxReached,
            tooltip: plan.maxReached ? t('workspace.addLuminaireManuallyDisabled') : undefined,
          },
        };
      }
    }
  }, [
    plan,
    t,
    actions,
    projectName,
    frontendPdfPlotsEnabled,
    pdfUrl,
    numPages,
    activeSection,
    goTo,
    confirmingAutoposition,
    appliedMatch,
  ]);
}

/** Targets met across all measured layers (each expected value with a matching actual). */
function countTargets(measurementData: ReturnType<typeof selectWorkspaceMeasurementData>): {
  satisfied: number;
  total: number;
} {
  let satisfied = 0;
  let total = 0;
  for (const layer of measurementData ?? []) {
    if (!layer.actualValues) continue;
    for (const expected of layer.expectedValues) {
      const actual = layer.actualValues.find((a) => a.function === expected.function && a.type === expected.type);
      if (!actual) continue;
      total++;
      const pair: ExpectedActualPair = {
        expected: expected.value,
        actual: actual.value,
        function: expected.function,
        type: expected.type,
      };
      if (isTargetSatisfied(pair)) satisfied++;
    }
  }
  return { satisfied, total };
}
